import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Calculator } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { formatMoney, isNegativeMoney, moneyLocale } from '../lib/money'
import type { CostDashboardStats } from '../types'

interface Props { stats: CostDashboardStats; projectId: string }

/** 1b-C spec §4: the project's cost forecast on the Nástěnka, for Cost Control viewers only (`costStats` is null otherwise). */
const CostControlCard: React.FC<Props> = ({ stats, projectId }) => {
  const { t, i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const money = (v: string) => formatMoney(v, { locale, currency: stats.currency, whole: true })
  return (
    <Link to={`/project/${projectId}/cost-control?tab=overview`}>
      <Card className="cursor-pointer p-4 transition-colors hover:bg-muted/50">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-2xl font-bold leading-none tabular-nums">{money(stats.projectedFinalCost)}</span>
            <h4 className="mt-1 text-sm font-medium text-foreground">{t('CostControl_dashboard_title')}</h4>
          </div>
          <Calculator className="h-8 w-8 text-muted-foreground/20" />
        </div>
        <dl className="mt-3 space-y-1 text-xs">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">{t('CostControl_col_revisedBudget')}</dt>
            <dd className="tabular-nums">{money(stats.revisedBudget)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">{t('CostControl_col_variance')}</dt>
            <dd className={cn('tabular-nums', isNegativeMoney(stats.variance) && 'text-destructive')}>{money(stats.variance)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">{t('CostControl_tab_pending')}</dt>
            <dd className="tabular-nums">{stats.pendingCount}</dd>
          </div>
        </dl>
      </Card>
    </Link>
  )
}
export default CostControlCard
